import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Maskito } from '@maskito/core';
import { maskitoUpdateElement } from '@maskito/core';
import { maskitoParseNumber, maskitoStringifyNumber } from '@maskito/kit';
import type { MaskitoNumberParams } from '@maskito/kit';

import { CadsChipButtonComponent } from '../../components/cads-chip-button/cads-chip-button.component';
import type { CadsChipButtonConfig } from '../../components/cads-chip-button/cads-chip-button.config';
import { nextCadsFormKitId } from '../internal/cads-form-kit-ids';
import {
  cadsClampCurrencyAmount,
  cadsCurrencyDisplayIsEmpty,
  cadsCurrencyEmptyDisplay,
  cadsCurrencyMaskitoOptions,
  cadsCurrencyNumberParams,
} from '../internal/cads-currency-maskito';

import type {
  CadsCurrencyAmountInputConfig,
  CadsCurrencyAmountQuickOption,
} from './cads-currency-amount-input.config';

/**
 * Hero-style USD amount entry for transfer and funding flows: large centered Maskito input, optional hint/error
 * caption, and optional quick-amount chips. Implements {@link ControlValueAccessor} with `number | null`.
 */
@Component({
  selector: 'cads-currency-amount-input',
  imports: [CadsChipButtonComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CadsCurrencyAmountInputComponent),
      multi: true,
    },
  ],
  templateUrl: './cads-currency-amount-input.component.html',
  styleUrl: './cads-currency-amount-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'cads-currency-amount-input-host',
  },
})
export class CadsCurrencyAmountInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  readonly config = input.required<CadsCurrencyAmountInputConfig>();

  private readonly _amountInputRef = viewChild<ElementRef<HTMLInputElement>>('amountInput');

  private readonly _fallbackInputId = nextCadsFormKitId('cads-currency-amount-input');

  private readonly _currentValue = signal<number | null>(null);
  private readonly _selectedChipIndex = signal<number | null>(null);
  private readonly _controlDisabled = signal(false);
  private readonly _focused = signal(false);
  private _maskito: Maskito | null = null;
  private _numberParams: MaskitoNumberParams = cadsCurrencyNumberParams();
  private _pendingValue: number | null | undefined;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _onChange: (value: number | null) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  protected readonly _inputId = computed(() => this._fallbackInputId);

  protected readonly _captionId = computed(() => `${this._inputId()}-caption`);

  protected readonly _disabled = computed(() => this._controlDisabled() || this.config().disabled === true);

  protected readonly _min = computed(() => this.config().min ?? 0);

  protected readonly _max = computed(() => this.config().max ?? Number.MAX_SAFE_INTEGER);

  protected readonly _hasError = computed(() => (this.config().errorMessage?.trim() ?? '').length > 0);

  protected readonly _captionText = computed(() => {
    const err = this.config().errorMessage?.trim();
    if (err) {
      return err;
    }
    return this.config().hint?.trim() ?? '';
  });

  protected readonly _showCaption = computed(() => this._captionText().length > 0);

  protected readonly _quickOptions = computed(() => this.config().quickOptions ?? []);

  protected readonly _isCompact = computed(() => this.config().density === 'compact');

  protected readonly _ariaLabel = computed(
    () => this.config().ariaLabel?.trim() || 'Enter amount'
  );

  protected readonly _showEmptyPlaceholder = computed(
    () => this._isDefaultEmptyValue(this._currentValue()) && !this._focused() && !this._disabled()
  );

  protected readonly _emptyPlaceholderDisplay = computed(() => {
    const fallback = this.config().defaultWhenEmpty;
    if (fallback === undefined) {
      return '';
    }
    return maskitoStringifyNumber(fallback, this._numberParams);
  });

  ngAfterViewInit(): void {
    this._initMaskito();
    if (this._pendingValue !== undefined) {
      this._applyWriteValue(this._pendingValue);
      this._pendingValue = undefined;
    } else if (this._currentValue() === null) {
      this._applyWriteValue(null);
      this._emitDefaultWhenEmptyIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this._clearDebounce();
    this._maskito?.destroy();
    this._maskito = null;
  }

  writeValue(value: number | string | null | undefined): void {
    const normalized =
      value === '' || value === undefined ? null : value === null ? null : Number(value);
    const coerced = normalized === null || Number.isFinite(normalized) ? normalized : null;
    this._currentValue.set(this._normalizeStoredValue(coerced));
    if (!this._maskito) {
      this._pendingValue = coerced;
      return;
    }
    this._applyWriteValue(coerced);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._controlDisabled.set(isDisabled);
  }

  protected _chipConfig(
    option: CadsCurrencyAmountQuickOption,
    index: number
  ): CadsChipButtonConfig {
    const selected = this._selectedChipIndex() === index;
    return {
      label: option.label,
      backgroundColor: selected ? 'var(--cads-color-blue)' : 'var(--cads-color-white)',
      foregroundColor: selected ? 'var(--cads-color-white)' : 'var(--cads-color-text)',
      disabled: option.disabled === true || this._disabled(),
    };
  }

  protected _onChipTap(option: CadsCurrencyAmountQuickOption, index: number): void {
    if (option.disabled || this._disabled()) {
      return;
    }
    const current = this._currentValue() ?? 0;
    let next: number;
    if (option.kind === 'set') {
      next = option.value;
    } else {
      next = current + option.delta;
    }
    next = cadsClampCurrencyAmount(next, this._min(), this._max());
    this._selectedChipIndex.set(index);
    this._setValueAndEmit(next, true);
  }

  protected _onInput(): void {
    this._selectedChipIndex.set(null);
    const parsed = this._parseInputValue();
    const stored = this._normalizeStoredValue(parsed);
    this._currentValue.set(stored);
    this._emitDebounced(stored);
  }

  protected _onKeydown(): void {
    this._selectedChipIndex.set(null);
  }

  protected _onFocus(): void {
    this._focused.set(true);
    if (this._isDefaultEmptyValue(this._currentValue())) {
      this._applyWriteValue(null);
    }
  }

  protected _onBlur(): void {
    this._focused.set(false);
    this._onTouched();
    const parsed = this._parseInputValue();
    if (this._isDefaultEmptyValue(parsed)) {
      this._setValueAndEmit(null, true);
    }
  }

  private _initMaskito(): void {
    const el = this._amountInputRef()?.nativeElement;
    if (!el) {
      return;
    }
    this._refreshMaskParams();
    this._maskito = new Maskito(el, cadsCurrencyMaskitoOptions(this._maskOverrides()));
  }

  private _maskOverrides() {
    return {
      precision: this.config().precision ?? 2,
      min: this._min(),
      max: this._max(),
      allowNegative: false,
    };
  }

  private _refreshMaskParams(): void {
    this._numberParams = cadsCurrencyNumberParams(this._maskOverrides());
  }

  private _parseInputValue(): number | null {
    const el = this._amountInputRef()?.nativeElement;
    if (!el) {
      return null;
    }
    const raw = el.value;
    if (cadsCurrencyDisplayIsEmpty(raw, this._numberParams)) {
      return null;
    }
    const parsed = maskitoParseNumber(raw, this._numberParams);
    if (Number.isNaN(parsed)) {
      return null;
    }
    return cadsClampCurrencyAmount(parsed, this._min(), this._max());
  }

  private _isDefaultEmptyValue(value: number | null): boolean {
    const fallback = this.config().defaultWhenEmpty;
    return fallback !== undefined && (value === null || value === fallback);
  }

  /** Maps model sentinel values (null / defaultWhenEmpty) to null for display. */
  private _normalizeStoredValue(value: number | null): number | null {
    return this._isDefaultEmptyValue(value) ? null : value;
  }

  private _displayValueFor(value: number | null): number | null {
    if (this._isDefaultEmptyValue(value)) {
      return null;
    }
    return value;
  }

  private _applyWriteValue(value: number | null): void {
    const el = this._amountInputRef()?.nativeElement;
    if (!el) {
      return;
    }
    const displayValue = this._displayValueFor(value);
    if (displayValue === null) {
      maskitoUpdateElement(el, cadsCurrencyEmptyDisplay(this._numberParams));
      return;
    }
    const clamped = cadsClampCurrencyAmount(displayValue, this._min(), this._max());
    maskitoUpdateElement(el, maskitoStringifyNumber(clamped, this._numberParams));
  }

  private _valueToEmit(stored: number | null): number | null {
    if (stored !== null) {
      return stored;
    }
    const fallback = this.config().defaultWhenEmpty;
    return fallback !== undefined ? fallback : null;
  }

  private _emitDefaultWhenEmptyIfNeeded(): void {
    const emitted = this._valueToEmit(this._currentValue());
    if (emitted !== null && this._currentValue() === null) {
      this._onChange(emitted);
    }
  }

  private _setValueAndEmit(value: number | null, immediate: boolean): void {
    const stored = this._normalizeStoredValue(value);
    this._currentValue.set(stored);
    this._applyWriteValue(stored);
    const emitted = this._valueToEmit(stored);
    if (immediate) {
      this._clearDebounce();
      this._onChange(emitted);
      return;
    }
    this._emitDebounced(stored);
  }

  private _emitDebounced(value: number | null): void {
    this._clearDebounce();
    const ms = this.config().debounceMs ?? 250;
    this._debounceTimer = setTimeout(() => {
      this._onChange(this._valueToEmit(value));
      this._debounceTimer = null;
    }, ms);
  }

  private _clearDebounce(): void {
    if (this._debounceTimer != null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  }
}
